import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import { ProductsOrder3P } from '../../constants/3p-product-configs/product-keys-3p';
import {
	is1pProductKey,
	is3pProductKey,
	type ProductKeys,
	type ProductKeys1P,
	PRODUCTS_ORDER,
} from '../../constants/product-keys';

/**
 * Domain patterns for different products
 * Maps domain patterns to ProductKeys (both 1P and 3P)
 */
const DOMAIN_TO_PRODUCT_MAP: Array<{ pattern: RegExp; product: ProductKeys }> = [
	// First-party Atlassian products
	{ pattern: /^(.*\.)?atlassian\.net$/i, product: 'confluence' as ProductKeys1P },
	{ pattern: /^(.*\.)?atlassian\.com$/i, product: 'confluence' as ProductKeys1P },
	{ pattern: /^(.*\.)?trello\.com$/i, product: 'trello' as ProductKeys1P },
	{ pattern: /^(.*\.)?bitbucket\.org$/i, product: 'bitbucket' as ProductKeys1P },

	// Google products (3P)
	{ pattern: /^(.*\.)?drive\.google\.com$/i, product: ProductKeys3P.GoogleDrive },
	{ pattern: /^(.*\.)?docs\.google\.com$/i, product: ProductKeys3P.GoogleDrive },
	{ pattern: /^(.*\.)?sheets\.google\.com$/i, product: ProductKeys3P.GoogleDrive },
	{ pattern: /^(.*\.)?slides\.google\.com$/i, product: ProductKeys3P.GoogleDrive },
	{ pattern: /^(.*\.)?calendar\.google\.com$/i, product: ProductKeys3P.GoogleCalendar },
	{ pattern: /^(.*\.)?mail\.google\.com$/i, product: ProductKeys3P.Gmail },
	{ pattern: /^(.*\.)?gmail\.com$/i, product: ProductKeys3P.Gmail },

	// Microsoft products (3P)
	{ pattern: /^(.*\.)?sharepoint\.com$/i, product: ProductKeys3P.Sharepoint },
	{ pattern: /^(.*\.)?onedrive\.live\.com$/i, product: ProductKeys3P.OneDrive },
	{ pattern: /^(.*\.)?1drv\.ms$/i, product: ProductKeys3P.OneDrive },
	{ pattern: /^(.*\.)?teams\.microsoft\.com$/i, product: ProductKeys3P.Teams },
	{ pattern: /^(.*\.)?outlook\.live\.com$/i, product: ProductKeys3P.OutlookMail },
	{ pattern: /^(.*\.)?outlook\.office\.com$/i, product: ProductKeys3P.OutlookCalendar },

	// Other popular products (3P)
	{ pattern: /^(.*\.)?slack\.com$/i, product: ProductKeys3P.Slack },
	{ pattern: /^(.*\.)?dropbox\.com$/i, product: ProductKeys3P.Dropbox },
	{ pattern: /^(.*\.)?github\.com$/i, product: ProductKeys3P.Github },
	{ pattern: /^(.*\.)?figma\.com$/i, product: ProductKeys3P.Figma },
	{ pattern: /^(.*\.)?loom\.com$/i, product: ProductKeys3P.Loom },
	{ pattern: /^(.*\.)?notion\.so$/i, product: ProductKeys3P.Notion },
	{ pattern: /^(.*\.)?asana\.com$/i, product: ProductKeys3P.Asana },
	{ pattern: /^(.*\.)?airtable\.com$/i, product: ProductKeys3P.Airtable },
	{ pattern: /^(.*\.)?smartsheet\.com$/i, product: ProductKeys3P.Smartsheet },
	{ pattern: /^(.*\.)?zendesk\.com$/i, product: ProductKeys3P.Zendesk },
	{ pattern: /^(.*\.)?miro\.com$/i, product: ProductKeys3P.Miro },
	{ pattern: /^(.*\.)?docusign\.com$/i, product: ProductKeys3P.Docusign },
	{ pattern: /^(.*\.)?lucidchart\.com$/i, product: ProductKeys3P.Lucid },
	{ pattern: /^(.*\.)?lucidspark\.com$/i, product: ProductKeys3P.Lucid },
	{ pattern: /^(.*\.)?monday\.com$/i, product: ProductKeys3P.Monday },
	{ pattern: /^(.*\.)?dev\.azure\.com$/i, product: ProductKeys3P.AzureDevops },
	{ pattern: /^(.*\.)?visualstudio\.com$/i, product: ProductKeys3P.AzureDevops },
	{ pattern: /^(.*\.)?gitlab\.com$/i, product: ProductKeys3P.Gitlab },
	{ pattern: /^(.*\.)?canva\.com$/i, product: ProductKeys3P.Canva },
	{ pattern: /^(.*\.)?box\.com$/i, product: ProductKeys3P.Box },
	{ pattern: /^(.*\.)?zeplin\.io$/i, product: ProductKeys3P.Zeplin },
	{ pattern: /^(.*\.)?servicenow\.com$/i, product: ProductKeys3P.ServiceNow },
	{ pattern: /^(.*\.)?freshservice\.com$/i, product: ProductKeys3P.Freshservice },
	{ pattern: /^(.*\.)?powerbi\.microsoft\.com$/i, product: ProductKeys3P.PowerBI },
	{ pattern: /^(.*\.)?clickup\.com$/i, product: ProductKeys3P.ClickUp },
	{ pattern: /^(.*\.)?amplitude\.com$/i, product: ProductKeys3P.Amplitude },
	{ pattern: /^(.*\.)?stripe\.com$/i, product: ProductKeys3P.Stripe },
	{ pattern: /^(.*\.)?adobe\.com$/i, product: ProductKeys3P.AdobeXD }, // Could be AdobeSign too
	{ pattern: /^(.*\.)?aha\.io$/i, product: ProductKeys3P.Aha },
	{ pattern: /^(.*\.)?mural\.co$/i, product: ProductKeys3P.Mural },
	{ pattern: /^(.*\.)?webex\.com$/i, product: ProductKeys3P.Webex },
	{ pattern: /^(.*\.)?todoist\.com$/i, product: ProductKeys3P.Todoist },
	{ pattern: /^(.*\.)?pagerduty\.com$/i, product: ProductKeys3P.PagerDuty },
	{ pattern: /^(.*\.)?pipedrive\.com$/i, product: ProductKeys3P.Pipedrive },
	{ pattern: /^(.*\.)?hubspot\.com$/i, product: ProductKeys3P.Hubspot },
	{ pattern: /^(.*\.)?dovetailapp\.com$/i, product: ProductKeys3P.Dovetail },
	{ pattern: /^(.*\.)?launchdarkly\.com$/i, product: ProductKeys3P.LaunchDarkly },
	{ pattern: /^(.*\.)?salesforce\.com$/i, product: ProductKeys3P.Salesforce },
	{ pattern: /^(.*\.)?sentry\.io$/i, product: ProductKeys3P.Sentry },
	{ pattern: /^(.*\.)?gitbook\.com$/i, product: ProductKeys3P.Gitbook },
	{ pattern: /^(.*\.)?amazonaws\.com$/i, product: ProductKeys3P.AWSS3 },
	{ pattern: /^(.*\.)?s3\..*\.amazonaws\.com$/i, product: ProductKeys3P.AWSS3 },
];

type LinkClassification = {
	url: string;
	product: ProductKeys | null;
	linkType: '3p' | '1p' | 'unknown';
};

export type { LinkClassification };

/**
 * Extracts the domain from a URL
 */
function extractDomain(url: string): string | null {
	try {
		const urlObj = new URL(url);
		return urlObj.hostname;
	} catch {
		return null;
	}
}

/**
 * Predicts the product type based on the URL domain
 */
function predictProductFromDomain(url: string): ProductKeys | null {
	const domain = extractDomain(url);
	if (!domain) {
		return null;
	}

	for (const { pattern, product } of DOMAIN_TO_PRODUCT_MAP) {
		if (pattern.test(domain)) {
			return product;
		}
	}

	return null;
}

/**
 * Classifies a URL into its type and product category
 */
function classifyLink(url: string): LinkClassification {
	const product = predictProductFromDomain(url);

	let linkType: '3p' | '1p' | 'unknown';

	if (product && is3pProductKey(product)) {
		linkType = '3p';
	} else if (product && is1pProductKey(product)) {
		linkType = '1p';
	} else {
		linkType = 'unknown';
	}

	return {
		url,
		product,
		linkType,
	};
}

/**
 * Gets the tier priority for a link classification
 * Special case: Loom is treated as 1P even though it's in ProductsOrder3P
 */
function getTierPriority(item: LinkClassification): number {
	// Special case: Loom should be treated as 1P
	if (item.product === ProductKeys3P.Loom) {
		return 1; // Treat Loom as 1P priority
	}

	if (item.linkType === '3p') {
		return 0;
	} // 3P links first
	if (item.linkType === '1p') {
		return 1;
	} // 1P links second
	return 2; // Unknown links last
}

/**
 * Reorders a list of URLs based on two-tier priority:
 * 1. 3P (Third-party) links - ordered by ProductsOrder3P
 * 2. 1P (First-party) links - ordered by the 1P portion of PRODUCTS_ORDER
 * 3. Unknown links - at the end
 *
 * @param urls - Array of URL strings to reorder
 * @returns Array of URLs reordered based on the two-tier priority system
 */
export function reorderLinksByProduct(urls: string[]): string[] {
	// Create order maps for faster lookup
	const fullProductOrderMap = new Map<ProductKeys, number>();
	PRODUCTS_ORDER.forEach((product, index) => {
		fullProductOrderMap.set(product, index);
	});

	// Classify URLs by their type and product
	const classifiedUrls = urls.map((url) => classifyLink(url));

	// Sort URLs based on two-tier priority system
	classifiedUrls.sort((a, b) => {
		// First tier: 3P vs 1P vs Unknown
		const tierA = getTierPriority(a);
		const tierB = getTierPriority(b);

		if (tierA !== tierB) {
			return tierA - tierB;
		}

		// Second tier: Within same tier, sort by product order
		if (a.linkType === '3p' && b.linkType === '3p' && a.product && b.product) {
			// For 3P products, use ProductsOrder3P ordering
			const order3pA = ProductsOrder3P.indexOf(a.product as ProductKeys3P);
			const order3pB = ProductsOrder3P.indexOf(b.product as ProductKeys3P);

			const priorityA = order3pA !== -1 ? order3pA : Number.MAX_SAFE_INTEGER;
			const priorityB = order3pB !== -1 ? order3pB : Number.MAX_SAFE_INTEGER;

			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}
		} else if (
			(a.linkType === '1p' && b.linkType === '1p' && a.product && b.product) ||
			a.product === ProductKeys3P.Loom ||
			b.product === ProductKeys3P.Loom
		) {
			// For 1P products or Loom (treated as 1P), use PRODUCTS_ORDER ordering
			// Special handling for Loom: position it right after Confluence
			let orderA: number;
			let orderB: number;

			if (a.product === ProductKeys3P.Loom) {
				// Position Loom right after Confluence (index 0), so give it index 0.5
				orderA = 0.5;
			} else {
				orderA = fullProductOrderMap.get(a.product!) ?? Number.MAX_SAFE_INTEGER;
			}

			if (b.product === ProductKeys3P.Loom) {
				orderB = 0.5;
			} else {
				orderB = fullProductOrderMap.get(b.product!) ?? Number.MAX_SAFE_INTEGER;
			}

			if (orderA !== orderB) {
				return orderA - orderB;
			}
		}

		// Third tier: If same type and product (or both unknown), maintain original order
		return 0;
	});

	return classifiedUrls.map((item) => item.url);
}
